import { InputLabel, Skeleton } from '@mui/material';
import React, { Component } from 'react';
import { safeTrackException } from '../../appInsights';

interface EwayFieldProps {
  type: string;
  attemptId: string;
  onChange?: (isValid: boolean, secureFieldCode: string) => void;
  onReady?: () => void;
  label?: string;
  sx?: any;
}

interface EwayFieldState {
  ewayInit: boolean;
  ewayFieldLoaded: boolean;
  focus: boolean;
}

export class EwayField extends Component<EwayFieldProps, EwayFieldState> {
  // Read at call time rather than module-eval time: config.js populates window.app.env from a plain
  // <script> in index.html, so a static initialiser would capture whatever happened to be set when
  // this module evaluated.
  //
  // There is deliberately NO hardcoded fallback key. A sandbox key silently standing in for the live
  // one still mints and saves one-time codes perfectly happily — the failure only surfaces later as
  // an opaque eWAY V6149 when the server tries to redeem a code the live account cannot resolve.
  // Failing visibly here is far cheaper to diagnose. The per-environment default lives in
  // configDefault.js, which is where an environment default belongs.
  static get ewayPublicApiKey(): string | null {
    const key =
      typeof window !== 'undefined' ? (window as any).app?.env?.EWAY_PUBLIC_API_KEY : undefined;

    if (!key) {
      safeTrackException(new Error('EWAY_PUBLIC_API_KEY is not set in config.js'), {
        component: 'EwayField',
      });
      return null;
    }

    return key;
  }
  ewayFieldRef: any;
  secureFieldCode: any;
  iframe: any;
  private setupDone = false;

  constructor(props: EwayFieldProps) {
    super(props);

    this.state = {
      ewayInit: window.eWAY,
      ewayFieldLoaded: false,
      focus: false,
    };

    this.ewayFieldRef = React.createRef();
    this.secureFieldCode = null;
  }

  componentDidMount() {
    if (!window.eWAY) this.initEway();
    else this.initEwaySecureField();
  }

  componentWillUnmount() {
    this.setupDone = false;
    if (this.iframe) {
      this.iframe.remove();
      this.iframe = null;
    }
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    if (prevProps.attemptId !== this.props.attemptId) {
      if (this.iframe) {
        this.iframe.remove();
        this.iframe = null;
      }
      this.setupDone = false;
      this.initEwaySecureField();
    }
  }

  initEwaySecureField() {
    if (this.setupDone) return;

    // Without a key we cannot mint a usable one-time code, so don't render a field that would look
    // functional and fail at checkout.
    const publicApiKey = EwayField.ewayPublicApiKey;
    if (!publicApiKey) return;

    this.setupDone = true;

    const { type, attemptId, onChange } = this.props;

    let style =
      'line-height: 1; height: 42px; border-radius: 28px; border: 1px solid #9ea2fa; color: #000; padding: 11px 16px; font-family: Inter, sans-serif; font-size: 16px; box-shadow:none;';
    if (type === 'expiry') {
      // The expiry secure field renders as native <select>s (Month + Year). They live in a
      // cross-origin eWAY iframe, so this `styles` string is our only styling hook. Strip the
      // default browser dropdown arrow and paint the app's shared chevron-double icon (the same
      // DropIcon used by AbstractFilterSelect) so each pill matches every other dropdown.
      //
      // Build the data URI with encodeURIComponent rather than a literal url("..."): eWAY
      // re-serialises this style string and mangles literal quotes, which silently drops the
      // background-image and leaves the field with no icon. encodeURIComponent escapes the quotes
      // (and spaces/#) so the URL survives that round-trip.
      const dropIcon =
        '<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><path d="M0 0h20v20H0z"></path><path d="M12.823 11.841a.833.833 0 0 1 1.1 1.248l-3.334 3.334-.078.069a.833.833 0 0 1-1.1-.07L6.077 13.09l-.069-.078a.833.833 0 0 1 .07-1.1l.078-.07a.833.833 0 0 1 1.1.07L10 14.654l2.744-2.743zM10.51 3.508l.078.07 3.334 3.333a.833.833 0 0 1-1.1 1.248l-.079-.07L10 5.346 7.256 8.089c-.3.3-.773.324-1.1.07l-.079-.07a.833.833 0 0 1-.069-1.1l.07-.078L9.41 3.577c.3-.3.773-.323 1.1-.069z" fill="#1B3DF2" fill-rule="nonzero"></path></g></svg>';
      const dropIconUrl = `data:image/svg+xml,${encodeURIComponent(dropIcon)}`;
      style +=
        'width: 47%; padding: 9px 40px 9px 16px; appearance: none; -webkit-appearance: none; -moz-appearance: none;' +
        ` background-image: url(${dropIconUrl}); background-repeat: no-repeat; background-position: right 12px center; background-size: 20px 20px;`;
    }

    if (window.eWAY) {
      window.eWAY.setupSecureField(
        {
          publicApiKey,
          fieldDivId: 'eway-secure-field-' + attemptId + '_' + type,
          autocomplete: true,
          fieldType: type,
          styles: style,
        },
        (event: any) => {
          this.setState({ focus: event.valueHasFocus });

          if (event.fieldValid) {
            this.secureFieldCode = event.secureFieldCode;
          }

          if (onChange) onChange(event.valueIsValid, event.secureFieldCode);
        }
      );

      if (this.ewayFieldRef.current) {
        const checkIframe = () => {
          const iframe = this.ewayFieldRef.current?.querySelector('iframe');
          if (iframe) {
            this.iframe = iframe;
            this.iframe.addEventListener('load', () => {
              this.setState({ ewayFieldLoaded: true });
              this.props.onReady?.();
            });
          } else {
            setTimeout(checkIframe, 50);
          }
        };
        checkIframe();
      }

      this.setState({ ewayInit: true });
    }
  }

  initEway() {
    if (window.eWAY) return;
    else EwayField.injectEwayScript(() => this.initEwaySecureField());
  }

  static injectEwayScript(onload: any) {
    if (window.eWAY) {
      onload();
      return;
    }

    let script: any = document.getElementById('eway-script');
    if (script) {
      if (onload) setTimeout(() => onload(), 100);
      return;
    }

    script = document.createElement('script');
    script.id = 'eway-script';
    if (onload) script.onload = onload;
    script.src = 'https://secure.ewaypayments.com/scripts/eWAY.min.js';
    script.setAttribute('data-init', 'false');
    document.body.appendChild(script);
  }

  static saveAllFields() {
    //window.eWAY.saveAllFields(() => {}, 2000);
  }

  static resetEwayFields() {
    window.eWAYUtils.setVar('activeFields', []);
    window.eWAYUtils.setVar('pageId', window.eWAYUtils.generateUUID());
  }

  onFocus() {
    this.setState({ focus: true });
  }

  render() {
    const { type, attemptId, label, sx } = this.props;
    const { focus } = this.state;

    return (
      <div
        className={
          'klayo-ewayfield klayo-ewayfield-' + type + (focus ? ' klayo-ewayfield--focus' : '')
        }
        style={sx}
      >
        <InputLabel sx={{ marginBottom: '6px' }}>{label}</InputLabel>
        <div
          id={'eway-secure-field-' + attemptId + '_' + type}
          className='klayo-ewayfield-ewayfield'
          ref={this.ewayFieldRef}
          onMouseDown={this.onFocus.bind(this)}
          style={{ display: window.eWAY ? 'block' : 'none' }}
        ></div>
        {!window.eWAY && <Skeleton height='42px' />}
      </div>
    );
  }
}
